/*
 * ─────────────────────────────────────────────────────────────────────────────
 * learnContent.js  —  Single source of truth for all Learn section content
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * HOW TO ADD A NEW GUIDE
 * ──────────────────────
 * 1. Add an entry to the GUIDES array below (copy the low-back-pain block).
 * 2. Set `available: true` and fill in a `path` (e.g. '/learn/shoulder').
 * 3. Write all content inside the `content` object (paragraphs, bullets,
 *    causes, tests, redFlags, management phases — all plain text arrays).
 * 4. Create the route in src/App.js:
 *      <Route path="/learn/shoulder" element={<GuideLayout id="shoulder" />} />
 * 5. That's it. The GuideLayout renderer reads this file and builds the page.
 *
 * HOW TO EDIT EXISTING CONTENT
 * ─────────────────────────────
 * Find the guide by `id` below and edit the text strings directly.
 * No JSX, no component knowledge needed.
 * ─────────────────────────────────────────────────────────────────────────────
 */

export const GUIDES = [

  /* ══════════════════════════════════════════════════════════════
     LOW BACK PAIN
  ══════════════════════════════════════════════════════════════ */
  {
    id: 'low-back-pain',
    available: true,
    path: '/learn/low-back-pain',

    /* ── Landing page card ── */
    card: {
      icon: '📚',
      tag: 'Available now',
      tagColor: '#059669',
      tagBg: '#f0fdf4',
      tagBorder: '#bbf7d0',
      title: 'Low Back Pain Assessment',
      sub: 'SLR, Slump test, McKenzie classification — full structured guide for LBP from history to discharge criteria.',
      tests: ['SLR', 'Slump Test', "Kemp's"],
    },

    /* ── Guide page ── */
    hero: {
      tags: [
        { label: 'Physiotherapy Guide', color: '#4f46e5', bg: '#eef2ff' },
        { label: 'Orthopaedic',         color: '#059669', bg: '#d1fae5' },
        { label: 'Low Back Pain',        color: '#92400e', bg: '#fef3c7' },
      ],
      title: 'Low Back Pain Assessment',
      subtitle: 'A Complete Physiotherapy Guide',
      description: 'Low back pain (LBP) is the leading cause of disability worldwide. This guide walks through a structured physiotherapy assessment — from subjective history to special orthopaedic tests — using the same clinical framework used in AssessMonk.',
      meta: ['📖 15 min read', '🔬 Evidence-based', '🩺 Clinician-written', '✅ Special tests included'],
    },

    content: {

      /* ── Section 1 ── */
      whatIs: {
        title: 'What is Low Back Pain?',
        paragraphs: [
          'Low back pain refers to pain, muscle tension, or stiffness localised below the costal margin and above the inferior gluteal folds, with or without leg pain. It is classified by duration (acute <6 weeks, subacute 6–12 weeks, chronic >12 weeks) and by pathoanatomical category.',
          'Approximately 85% of LBP presentations are non-specific — meaning no clear structural cause can be identified on imaging. Physiotherapy assessment focuses on identifying pain drivers, movement impairments, neurological involvement, and psychosocial factors that influence recovery.',
        ],
        classificationCards: [
          { label: 'Acute',     desc: '< 6 weeks',     colorKey: 'red'    },
          { label: 'Subacute',  desc: '6 – 12 weeks',  colorKey: 'amber'  },
          { label: 'Chronic',   desc: '> 12 weeks',    colorKey: 'green'  },
          { label: 'Recurrent', desc: 'Episodes > 1',  colorKey: 'indigo' },
        ],
      },

      /* ── Section 2 ── */
      keySymptoms: {
        title: 'Key Symptoms to Assess',
        intro: 'A thorough subjective history identifies the pain mechanism, irritability, and whether neurological structures are involved.',
        bullets: [
          'Location — central, unilateral, or bilateral; referral into buttock or leg',
          'Onset — gradual vs. sudden; mechanism of injury (lifting, flexion, rotation)',
          'Nature — dull ache, sharp, burning, or constant; intermittent vs. constant',
          'Aggravating factors — flexion (disc), extension (facet/stenosis), prolonged posture',
          'Relieving factors — rest, walking, position change, heat',
          'Morning stiffness > 30 minutes may suggest inflammatory pathology',
          'Neurological symptoms — paraesthesia, weakness, saddle anaesthesia, bladder/bowel changes',
          'Functional limitation — ability to sit, stand, walk, sleep, work',
        ],
      },

      /* ── Section 3 ── */
      commonCauses: {
        title: 'Common Causes',
        causes: [
          { label: 'Lumbar Disc Herniation',       tags: ['Radiculopathy', 'L4–S1 most common'],        colorKey: 'red'    },
          { label: 'Facet Joint Dysfunction',       tags: ['Extension-loaded', 'Referred to buttock'],   colorKey: 'amber'  },
          { label: 'Lumbar Spinal Stenosis',        tags: ['Neurogenic claudication', 'Older adults'],   colorKey: 'violet' },
          { label: 'Sacroiliac Joint Dysfunction',  tags: ['Posterior pelvis', 'PPPP/thigh thrust +ve'], colorKey: 'indigo' },
          { label: 'Myofascial / Non-specific',     tags: ['No clear structure', '85% of LBP'],          colorKey: 'green'  },
          { label: 'Spondylolisthesis',             tags: ['Hyperlordosis', 'Extension pain'],           colorKey: 'cyan'   },
        ],
      },

      /* ── Section 4 ── */
      objectiveAssessment: {
        title: 'Objective Assessment',
        intro: 'Objective examination for LBP includes observation, range of motion, neurological screening, palpation, and provocation tests.',
        domains: [
          { icon: '👁',  label: 'Observation',        items: ['Posture & list', 'Muscle atrophy', 'Antalgic gait'] },
          { icon: '📐', label: 'Range of Motion',     items: ['Flexion / extension', 'Lateral flexion', 'Pain arc'] },
          { icon: '🖐', label: 'Neurological Screen', items: ['L3–S1 dermatomes', 'Myotome testing', 'Reflexes (knee, Achilles)'] },
          { icon: '🖐', label: 'Palpation',           items: ['Paraspinal tenderness', 'PPIVM assessment', 'SI joint provocation'] },
        ],
      },

      /* ── Section 5 ── */
      specialTests: {
        title: 'Special Orthopaedic Tests',
        intro: 'The following tests are used to differentiate pain sources and identify neurological compromise in LBP presentations.',
        tests: [
          {
            name: 'Straight Leg Raise (SLR)',
            method: 'Patient supine. Passively raise the straight leg. Record angle at symptom onset.',
            positive: 'Radicular pain reproduced between 30°–70° — not just hamstring tightness',
            sensitivity: '80%',
            specificity: '40%',
          },
          {
            name: 'Slump Test',
            method: 'Patient seated. Slump forward, flex neck, extend knee, dorsiflex ankle. Extend neck to differentiate.',
            positive: 'Radicular symptoms reproduced and relieved by neck extension',
            sensitivity: '84%',
            specificity: '83%',
          },
          {
            name: "Femoral Nerve Stretch (FNST)",
            method: 'Patient prone. Passively flex knee — assesses L2–L4 nerve roots.',
            positive: 'Anterior thigh pain reproduced',
            sensitivity: '84%',
            specificity: '83%',
          },
          {
            name: "FABER Test (Patrick's)",
            method: 'Supine — hip in Flexion, ABduction, External Rotation. Overpressure to knee.',
            positive: 'Groin or SI joint pain reproduced',
            sensitivity: '77%',
            specificity: '72%',
          },
          {
            name: 'Posterior Pelvic Pain Provocation (PPPP)',
            method: 'Supine, hip flexed 90°. Apply longitudinal compression through femur.',
            positive: 'Deep posterior pelvic pain reproduced — indicates SI joint involvement',
            sensitivity: '81%',
            specificity: '80%',
          },
        ],
      },

      /* ── Section 6 ── */
      redFlags: {
        title: 'Red Flags — Exclude First',
        intro: 'Always screen for serious pathology before proceeding with routine physiotherapy management.',
        flags: [
          'Saddle anaesthesia, bladder or bowel dysfunction → Cauda equina syndrome — emergency referral',
          'Unexplained weight loss, night sweats, fever, age >50 with new onset → Screen for malignancy or infection',
          'History of cancer, IV drug use, immunosuppression → Urgent medical review',
          'Progressive bilateral neurological deficit → Urgent imaging and specialist referral',
          'Severe unrelenting night pain unaffected by position → Exclude visceral referral or bone pathology',
        ],
      },

      /* ── Section 7 ── */
      management: {
        title: 'Physiotherapy Management Framework',
        intro: 'Treatment is phase-based and guided by irritability, pain drivers, and functional goals.',
        phases: [
          {
            phase: 'Acute (0–6 wks)',
            colorKey: 'red',
            items: [
              'Relative rest — avoid provocative postures',
              'Pain education (reassure — LBP is benign)',
              'Gentle mobility: pelvic tilts, knee-to-chest',
              'Manual therapy: soft tissue, mobilisation',
              'Ice or heat for symptom relief',
            ],
          },
          {
            phase: 'Subacute (6–12 wks)',
            colorKey: 'amber',
            items: [
              'Progressive loading of lumbar extensors and flexors',
              'Core stability: transverse abdominus activation',
              'Graded walking program — 10 min → 30 min daily',
              'Address ergonomics and work posture',
              'Hydrotherapy if available',
            ],
          },
          {
            phase: 'Chronic (> 12 wks)',
            colorKey: 'green',
            items: [
              'Pain neuroscience education',
              'High-load progressive resistance training',
              'Aerobic conditioning (walking, cycling, swimming)',
              'Address psychological factors: fear-avoidance, catastrophising',
              'Outcome measure: Oswestry Disability Index (ODI)',
            ],
          },
        ],
      },

      /* ── CTA ── */
      cta: {
        icon: '🦴',
        heading: 'Ready to assess a real LBP case?',
        body: "Use AssessMonk's structured orthopaedic form to enter ROM, special test results, and MMT grades — then let the AI generate a complete differential diagnosis and phased treatment plan.",
        buttonLabel: 'Try AI-based LBP Assessment →',
      },
    },
  },

  /* ══════════════════════════════════════════════════════════════
     SHOULDER IMPINGEMENT  (coming soon — fill content when ready)
  ══════════════════════════════════════════════════════════════ */
  {
    id: 'shoulder-impingement',
    available: false,
    path: null,

    card: {
      icon: '🦴',
      tag: 'Coming soon',
      tagColor: '#d97706',
      tagBg: '#fffbeb',
      tagBorder: '#fde68a',
      title: 'Shoulder Impingement',
      sub: "Hawkins-Kennedy, Neer's, empty can — rotator cuff assessment with clinical reasoning for impingement vs tear.",
      tests: ["Hawkins-Kennedy", "Neer's", 'Empty Can'],
    },

    hero: {
      tags: [
        { label: 'Physiotherapy Guide', color: '#4f46e5', bg: '#eef2ff' },
        { label: 'Orthopaedic',         color: '#059669', bg: '#d1fae5' },
        { label: 'Shoulder',            color: '#d97706', bg: '#fef3c7' },
      ],
      title: 'Shoulder Impingement Assessment',
      subtitle: 'A Complete Physiotherapy Guide',
      description: 'Subacromial impingement is the most common cause of shoulder pain in clinical practice. This guide covers the full structured assessment — from rotator cuff testing to functional outcome measures.',
      meta: ['📖 12 min read', '🔬 Evidence-based', '🩺 Clinician-written', '✅ Special tests included'],
    },

    /* Add content here when writing the guide */
    content: {},
  },

  /* ══════════════════════════════════════════════════════════════
     STROKE REHABILITATION  (coming soon)
  ══════════════════════════════════════════════════════════════ */
  {
    id: 'stroke-rehabilitation',
    available: false,
    path: null,

    card: {
      icon: '🧠',
      tag: 'Coming soon',
      tagColor: '#7c3aed',
      tagBg: '#f5f3ff',
      tagBorder: '#ddd6fe',
      title: 'Stroke Rehabilitation',
      sub: 'UMN patterns, Brunnstrom stages, tone management strategies and functional goal-setting frameworks.',
      tests: ['Berg Balance', 'FIM', 'Barthel'],
    },

    hero: {
      tags: [
        { label: 'Physiotherapy Guide', color: '#4f46e5', bg: '#eef2ff' },
        { label: 'Neurological',        color: '#7c3aed', bg: '#f5f3ff' },
        { label: 'Stroke / CVA',        color: '#be185d', bg: '#fdf2f8' },
      ],
      title: 'Stroke Rehabilitation Assessment',
      subtitle: 'A Complete Physiotherapy Guide',
      description: 'Stroke is the leading cause of acquired adult disability. This guide covers neurological physiotherapy assessment — lesion localisation, tone grading, functional outcome tools, and evidence-based rehab priorities.',
      meta: ['📖 18 min read', '🔬 Evidence-based', '🩺 Clinician-written', '✅ Outcome measures included'],
    },

    content: {},
  },

  /* ══════════════════════════════════════════════════════════════
     COPD PHYSIOTHERAPY  (coming soon)
  ══════════════════════════════════════════════════════════════ */
  {
    id: 'copd-physiotherapy',
    available: false,
    path: null,

    card: {
      icon: '🫁',
      tag: 'Coming soon',
      tagColor: '#0891b2',
      tagBg: '#ecfeff',
      tagBorder: '#a5f3fc',
      title: 'COPD Physiotherapy',
      sub: 'Airway clearance techniques, dyspnoea assessment, exercise prescription and pulmonary rehab progression.',
      tests: ['6MWT', 'Borg RPE', 'MRC Dyspnoea'],
    },

    hero: {
      tags: [
        { label: 'Physiotherapy Guide',  color: '#4f46e5', bg: '#eef2ff' },
        { label: 'Cardiopulmonary',      color: '#0891b2', bg: '#ecfeff' },
        { label: 'COPD',                 color: '#065f46', bg: '#d1fae5' },
      ],
      title: 'COPD Physiotherapy Assessment',
      subtitle: 'A Complete Physiotherapy Guide',
      description: 'COPD is a progressive respiratory condition requiring structured physiotherapy management. This guide covers airway clearance, exercise tolerance assessment, dyspnoea grading, and pulmonary rehabilitation principles.',
      meta: ['📖 14 min read', '🔬 Evidence-based', '🩺 Clinician-written', '✅ Outcome measures included'],
    },

    content: {},
  },

];

/* ─── Helper: get a single guide by id ─── */
export const getGuide = (id) => GUIDES.find(g => g.id === id) ?? null;
